import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Zap, ExternalLink, MapPin } from 'lucide-react';
import { WidgetStatus } from '@techstack/ui';
import apiClient from '../../lib/apiClient';
import { AgencyLogo } from './AgencyLogo';
import { fmtMoney, accountKey, rowSpendText } from './accountsFormat';
// ONE renewal row, shared with the Procurement tab — see ./renewals.js.
import { RenewalRow, sortRenewals } from './renewals';

/*
 * AccountsOverviewIntel — the non-chart half of GAS's Customer Overview.
 *
 * Ladder S2 in docs/ACCOUNTS-GAS-PARITY.md: identity band, fact chips, key
 * renewals, procurement summary + funding, and the business intel panels
 * (drivers, objectives, partners, constituents). Same order GAS renders them.
 *
 * One deliberate divergence: GAS marks each driver with a ⚡ emoji. TekStak's
 * design language does not use emoji as icons, so this uses the lucide glyph
 * the rest of the app uses. Same information, this app's vocabulary.
 *
 * Data (all now reachable with the browser's JWT — see S0):
 *   GET /api/v1/accounts/:account/profile     identity, facts, business intel
 *   GET /api/v1/renewals?account=&months=24   key renewals
 *   GET /api/v1/procurement/summary?account=  top vendors / categories
 *
 * Every panel is independent: one failing query shows its own note and the
 * rest of the page still works. A panel with nothing to say renders nothing,
 * rather than an empty frame.
 */


function fmtDay(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A 0–1 score and an ISO timestamp are engineering values; GAS shows a
 *  qualitative label and a human date instead (its DASH-7 note). Same here. */
function confidenceLabel(c) {
  if (c == null) return null;
  if (c >= 0.8) return 'High';
  if (c >= 0.5) return 'Medium';
  return 'Low';
}

function PanelNote({ message }) {
  return <div className="accounts-error"><AlertCircle size={14} /> {message}</div>;
}

/**
 * `section` selects ONE panel so the account board can place the identity band,
 * key renewals, the procurement summary, funding and the business intel as
 * independent widgets. Omitted renders the whole stack, unchanged. The queries
 * dedupe across instances, so five widgets cost one fetch each.
 */
export default function AccountsOverviewIntel({ selectedDepartment, section = null, onOpen = null }) {
  const wants = (name) => section === null || section === name;
  const account = accountKey(selectedDepartment);
  const departmentId = selectedDepartment?.department_id;

  const profileQuery = useQuery({
    queryKey: ['accounts-profile', account],
    queryFn: () => apiClient.get(`/api/v1/accounts/${encodeURIComponent(account)}/profile`),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  const renewalsQuery = useQuery({
    queryKey: ['accounts-renewals', account],
    queryFn: () => apiClient.get('/api/v1/renewals', { params: { account, months: 24 } }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  const summaryQuery = useQuery({
    queryKey: ['accounts-procurement-summary', account],
    queryFn: () => apiClient.get('/api/v1/procurement/summary', { params: { account } }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  const p = profileQuery.data?.found ? profileQuery.data : null;
  const f = p?.facts || {};
  const drivers = p?.businessDrivers || [];
  const objectives = p?.businessObjectives || [];
  const partners = (p?.keyPartners || []).map((x) => x && (x.name || x)).filter(Boolean);
  const constituents = (p?.constituents || []).map((x) => x && (x.group || x)).filter(Boolean);

  const renewalRows = sortRenewals(renewalsQuery.data?.renewals || []);
  const summary = summaryQuery.data || null;
  const topVendors = summary?.topVendors || summary?.top_vendors || [];
  const topCategories = summary?.topCategories || summary?.top_categories || [];

  const confidence = confidenceLabel(p?.confidence);

  /**
   * What this ONE section has to say when it has no content to draw.
   *
   * These panels were written for a stack, where a panel with nothing to say
   * correctly renders nothing — it simply isn't on the page. On a board the
   * cell is already drawn, so the same `{data && ...}` gate produces a titled
   * card with a blank body. Identity was blank for the whole of first paint;
   * Renewals was blank for any account with no expiring paper.
   */
  const sectionStatus = () => {
    const profileGate = (emptyMessage) => {
      if (profileQuery.isLoading) return { state: 'loading' };
      if (profileQuery.error) {
        return {
          state: 'error',
          message: `Account profile unavailable — ${profileQuery.error.message}`,
          onRetry: () => profileQuery.refetch(),
        };
      }
      if (!p) return { state: 'empty', message: 'No profile recorded for this agency yet.' };
      return emptyMessage ? { state: 'empty', message: emptyMessage } : null;
    };

    switch (section) {
      case 'identity':
        return profileGate(null);
      case 'funding':
        return profileGate(p && !p.fundingModel ? 'No funding model recorded.' : null);
      case 'business-drivers':
        return profileGate(
          p && drivers.length === 0 && objectives.length === 0
            ? 'No drivers or objectives recorded for this agency yet.'
            : null,
        );
      case 'renewals':
        if (renewalsQuery.isLoading) return { state: 'loading' };
        if (renewalsQuery.error) {
          return {
            state: 'error',
            message: `Renewals unavailable — ${renewalsQuery.error.message}`,
            onRetry: () => renewalsQuery.refetch(),
          };
        }
        if (renewalRows.length === 0) {
          return {
            state: 'empty',
            message: 'Nothing expires at this agency in the next 24 months.',
          };
        }
        return null;
      case 'procurement-summary':
        if (summaryQuery.isLoading) return { state: 'loading' };
        if (summaryQuery.error) {
          return {
            state: 'error',
            message: `Procurement summary unavailable — ${summaryQuery.error.message}`,
            onRetry: () => summaryQuery.refetch(),
          };
        }
        if (!summary) return { state: 'empty', message: 'No procurement recorded for this agency.' };
        return null;
      default:
        return null;
    }
  };

  const status = section ? sectionStatus() : null;
  if (status) return <WidgetStatus {...status} />;

  return (
    <>
      {p && wants('identity') && (
        <div className="accounts-profile">
          <div className="accounts-identity-main">
            <AgencyLogo
              url={p.logoUrl}
              acronym={p.acronym || account}
              logoClass="accounts-identity-logo"
              badgeClass="accounts-identity-badge"
            />
            {/* Named and flexed: as a bare <div> this was a flex item with
                neither `flex` nor `min-width:0`, so it shrink-wrapped instead
                of claiming the row — the agency name wrapped onto two lines
                with dead space beside it, at every width. */}
            <div className="accounts-identity-text">
              <h2 className="accounts-identity-title">
                {p.acronym || account}
                {p.account && p.account !== p.acronym && (
                  <span className="accounts-identity-sub">{p.account}</span>
                )}
              </h2>
              {p.mandate && (
                <p className="accounts-identity-mandate cq-clamp">{p.mandate}</p>
              )}
            </div>
            {confidence && (
              <span className="accounts-confidence">
                {confidence} confidence
                {p.enrichedAt && ` · updated ${fmtDay(p.enrichedAt)}`}
              </span>
            )}
          </div>

          <div className="accounts-facts cq-tray">
            {f.employeeCountRange && (
              <span className="accounts-fact"><span className="k">Employees</span> {f.employeeCountRange}</span>
            )}
            {f.annualBudgetRange && (
              <span className="accounts-fact"><span className="k">Budget</span> {f.annualBudgetRange}</span>
            )}
            {f.revenueGenerating != null && (
              <span className="accounts-fact">
                <span className="k">{f.revenueGenerating ? 'Revenue-generating' : 'Appropriated'}</span>
              </span>
            )}
            {f.spend3y != null && (
              <span className="accounts-fact"><span className="k">3-yr spend</span> {fmtMoney(f.spend3y)}</span>
            )}
            {f.website && (
              <span className="accounts-fact">
                <a href={f.website} target="_blank" rel="noreferrer">
                  {String(f.website).replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  <ExternalLink size={11} />
                </a>
              </span>
            )}
            {f.headquartersAddress && (
              <span className="accounts-fact"><MapPin size={12} /> {f.headquartersAddress}</span>
            )}
          </div>
        </div>
      )}
      {profileQuery.error && wants('identity') && (
        <PanelNote message={`Account profile unavailable — ${profileQuery.error.message}`} />
      )}

      {renewalRows.length > 0 && wants('renewals') && (
        <div className="ts-card accounts-panel-solo">
          {/* On the board the shell titles this "Renewals", so the heading
              would be the second one — but the WINDOW is not decoration. This
              list runs a fixed 24-month lookahead and does not follow the
              global time filter, so the eyebrow stays either way. */}
          {section === null ? (
            <h3 className="ts-card-title">
              Key renewals<span className="accounts-eyebrow">next 24 months</span>
            </h3>
          ) : (
            <div className="accounts-eyebrow accounts-eyebrow-block">next 24 months</div>
          )}
          {renewalRows.slice(0, 6).map((rn, i) => (
            <RenewalRow rn={rn} departmentId={departmentId} key={i} />
          ))}
          {/* Was "N more — see the Competitive tab", which was dead text AND
              named the wrong tab: no tab rendered renewals at all, so the
              remainder was unreachable. It is a control now, pointed at the
              Procurement tab that actually holds them. */}
          {renewalRows.length > 6 && (
            onOpen ? (
              <button type="button" className="accounts-more-btn" onClick={onOpen}>
                Show {renewalRows.length - 6} more →
              </button>
            ) : (
              <p className="accounts-panel-note">
                {renewalRows.length - 6} more below.
              </p>
            )
          )}
        </div>
      )}
      {renewalsQuery.error && wants('renewals') && (
        <PanelNote message={`Renewals unavailable — ${renewalsQuery.error.message}`} />
      )}

      {((summary && wants('procurement-summary')) || (p?.fundingModel && wants('funding'))) && (
        <div className={section ? 'accounts-bento accounts-bento--single' : 'accounts-bento'}>
          {summary && wants('procurement-summary') && (
            <div className="ts-card">
              <h3 className="ts-card-title">Procurement summary</h3>
              <div className="accounts-detail">
                <span className="k">Total spend</span>
                <span className="v">{summary.totalSpendFormatted || fmtMoney(summary.totalSpend)}</span>
              </div>
              {topVendors.length > 0 && (
                <p className="accounts-note-block">
                  <b>Top vendors:</b>{' '}
                  {topVendors.slice(0, 6).map((v) => `${v.vendor || v.name} ${rowSpendText(v)}`).join('  ·  ')}
                </p>
              )}
              {topCategories.length > 0 && (
                <p className="accounts-note-block">
                  <b>Top categories:</b>{' '}
                  {topCategories.slice(0, 6).map((c) => `${c.category || c.name} ${rowSpendText(c)}`).join('  ·  ')}
                </p>
              )}
            </div>
          )}
          {p?.fundingModel && wants('funding') && (
            <div className="ts-card">
              <h3 className="ts-card-title">Funding</h3>
              <p className="accounts-funding">{p.fundingModel}</p>
            </div>
          )}
        </div>
      )}

      {(drivers.length > 0 || objectives.length > 0) && wants('business-drivers') && (
        <div className={section ? 'accounts-bento accounts-bento--single' : 'accounts-bento'}>
          {drivers.length > 0 && (
            <div className="ts-card">
              <h3 className="ts-card-title">
                What&apos;s driving them<span className="accounts-eyebrow">talk tracks</span>
              </h3>
              <div className="accounts-drivers">
                {drivers.slice(0, 5).map((d, i) => (
                  <div className="accounts-driver" key={i}>
                    <Zap size={14} className="accounts-driver-icon" />
                    <div>
                      <div className="accounts-driver-title">{d.driver || d}</div>
                      {d.why_it_matters && (
                        <div className="accounts-driver-why">{d.why_it_matters}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(objectives.length > 0 || partners.length > 0 || constituents.length > 0) && (
            <div className="ts-card">
              {objectives.length > 0 && (
                <>
                  <h3 className="ts-card-title">Strategic objectives</h3>
                  <ul className="accounts-objectives">
                    {objectives.slice(0, 6).map((o, i) => (
                      <li key={i}>{o.objective || o}</li>
                    ))}
                  </ul>
                </>
              )}
              {partners.length > 0 && (
                <>
                  <div className="accounts-eyebrow accounts-eyebrow-block">Key partners</div>
                  <div className="accounts-chip-row">
                    {partners.slice(0, 8).map((x, i) => <span className="accounts-chip" key={i}>{x}</span>)}
                  </div>
                </>
              )}
              {constituents.length > 0 && (
                <>
                  <div className="accounts-eyebrow accounts-eyebrow-block">Constituents</div>
                  <div className="accounts-chip-row">
                    {constituents.slice(0, 8).map((x, i) => <span className="accounts-chip" key={i}>{x}</span>)}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
